import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { getDocuments, deleteDocument, updateDocument } from '../services/database';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Modal } from '../components/common/Modal';
import {
  Search,
  Calendar as CalendarIcon,
  User,
  Receipt as ReceiptIcon,
  Trash2,
  TrendingUp,
  Hash,
  X,
  MessageCircle,
  Printer,
  FileDown
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';
// import { useReactToPrint } from 'react-to-print'; // No necesario, usando solución personalizada
import { shareReceiptPDF } from '../utils/pdfGenerator';

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(5, 150, 105, 0.09) 100%);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(16, 185, 129, 0.18);

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.22) 0%, transparent 65%);
    pointer-events: none;
  }

  h1 {
    position: relative;
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #047857 0%, #10B981 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    position: relative;
    margin: 4px 0 0;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 14px;
    margin-bottom: 14px;
    border-radius: 16px;

    h1 {
      font-size: 1.4rem;
      letter-spacing: -0.5px;
    }
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-bottom: 14px;
  }
`;

const StatCard = styled.div`
  position: relative;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 12px -6px rgba(46, 91, 136, 0.08);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${props => props.$accent || 'linear-gradient(180deg, #10B981, #059669)'};
  }

  .label {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #64748b;
    margin-bottom: 4px;
  }

  .value {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.5px;
    background: ${props => props.$gradient || 'linear-gradient(135deg, #047857, #10B981)'};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 10px 12px;
    border-radius: 12px;

    .label { font-size: 9.5px; }
    .value { font-size: 15px; letter-spacing: -0.3px; }
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  align-items: stretch;
  padding: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 16px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 4px 14px -6px rgba(46, 91, 136, 0.08);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 10px;
    gap: 8px;
    border-radius: 14px;
  }
`;

const InputWrap = styled.div`
  position: relative;
  flex: ${props => props.$grow || 1};
  min-width: ${props => props.$min || '180px'};

  svg.field-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: #94a3b8;
    pointer-events: none;
  }

  input {
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 1px solid rgba(148, 163, 184, 0.28);
    border-radius: 10px;
    background: #ffffff;
    font-size: 14px;
    color: #1e293b;
    font-weight: 500;
    transition: border-color 160ms ease, box-shadow 160ms ease;

    &:focus {
      outline: none;
      border-color: #10B981;
      box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
    }
  }
`;

const SalesGrid = styled.div`
  display: grid;
  gap: 14px;
`;

const SaleCard = styled.div`
  position: relative;
  padding: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdfe 100%);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  overflow: hidden;
  isolation: isolate;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 20px -8px rgba(46, 91, 136, 0.1);
  transition:
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 260ms ease,
    border-color 260ms ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #047857 0%, #10B981 50%, #047857 100%);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 170px;
    height: 170px;
    background: radial-gradient(circle at top right, rgba(16, 185, 129, 0.08) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(16, 185, 129, 0.35);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 1px 3px rgba(15, 23, 42, 0.05),
      0 14px 32px -10px rgba(46, 91, 136, 0.16),
      0 4px 10px -2px rgba(16, 185, 129, 0.12);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 14px;
    border-radius: 14px;

    &::before { height: 3px; }
  }
`;

const SaleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-top: 6px;
  margin-bottom: 14px;
`;

const SaleInfo = styled.div`
  flex: 1;
  min-width: 0;

  .sale-id {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(5, 150, 105, 0.2));
    color: #047857;
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    margin-bottom: 6px;

    svg { width: 12px; height: 12px; stroke-width: 2.5; }
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #64748b;
    font-size: 12.5px;
    font-weight: 500;
    margin-top: 4px;

    svg {
      width: 13px;
      height: 13px;
      color: #94a3b8;
      flex-shrink: 0;
    }
  }

  .customer {
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SaleTotal = styled.div`
  text-align: right;
  flex-shrink: 0;

  .total {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.6px;
    background: linear-gradient(135deg, #047857 0%, #10B981 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  .tax-info {
    display: inline-block;
    margin-top: 6px;
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    background: rgba(79, 179, 191, 0.12);
    color: #0E7490;
    border: 1px solid rgba(79, 179, 191, 0.28);
    border-radius: 999px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    .total { font-size: 18px; }
    .tax-info { font-size: 9px; }
  }
`;

const ItemsList = styled.div`
  border-top: 1px dashed rgba(148, 163, 184, 0.35);
  margin-top: 14px;
  padding: 12px 0 0;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
  font-size: 13px;
  color: #475569;

  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  .quantity {
    color: #94a3b8;
    font-weight: 600;
    margin-left: 4px;
  }

  .price {
    color: #1e293b;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
`;

const EmptyState = styled.div`
  padding: 48px 20px;
  text-align: center;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px dashed rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
`;

const Receipt = styled.div`
  padding: 20px;
  max-width: 350px;
  margin: 0 auto;
  font-family: 'Courier New', monospace;
  background: white;
  color: black;
  font-size: 14px;
  
  @media print {
    margin: 0;
    padding: 0;
    max-width: 100%;
  }
`;

const ReceiptHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    font-size: 18px;
  }
  
  p {
    margin: 2px 0;
    font-size: 12px;
  }
`;

const ReceiptBody = styled.div`
  margin-bottom: 20px;
`;

const ReceiptItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 12px;
`;

const ReceiptFooter = styled.div`
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed #000;
  
  p {
    margin: 2px 0;
    font-size: 12px;
  }
`;

const ConfirmModal = styled.div`
  p {
    margin-bottom: ${props => props.theme.spacing.lg};
  }
  
  .warning {
    background-color: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.3);
    border-radius: ${props => props.theme.borderRadius.md};
    padding: ${props => props.theme.spacing.md};
    margin-bottom: ${props => props.theme.spacing.lg};
    color: #f39c12;
  }
`;

const ProfitInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(168, 85, 247, 0.12) 100%);
  border: 1px solid rgba(168, 85, 247, 0.25);
  border-radius: 10px;
  font-size: 13px;
  color: #6B21A8;

  .icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(139, 92, 246, 0.35);

    svg { width: 14px; height: 14px; stroke-width: 2.4; }
  }

  .profit-content {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .profit-label {
    font-weight: 600;
    color: #7E22CE;
    font-size: 12.5px;
  }

  .profit-value {
    font-weight: 800;
    letter-spacing: -0.3px;
    background: linear-gradient(135deg, #7E22CE 0%, #A855F7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
    font-size: 14px;
  }

  .margin-pct {
    padding: 2px 8px;
    background: rgba(168, 85, 247, 0.16);
    border: 1px solid rgba(168, 85, 247, 0.3);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: #7E22CE;
  }

  @media print {
    display: none;
  }
`;

export const SalesHistory = () => {
  const { userId, userData } = useAuth();
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    if (userId) {
      loadSales();
      loadProducts();
    }
  }, [userId]);

  const loadProducts = async () => {
    try {
      const productsData = await getDocuments(userId, 'products');
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadSales = async () => {
    try {
      setLoading(true);
      const salesData = await getDocuments(userId, 'sales', [], 'createdAt', 'desc');
      setSales(salesData);
    } catch (error) {
      console.error('Error loading sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSaleMargin = (sale) => {
    if (!sale || !sale.items || products.length === 0) return null;
    
    let totalCost = 0;
    let totalRevenue = 0;
    
    sale.items.forEach(item => {
      const product = products.find(p => p.id === item.productId);
      const itemCost = product ? (product.cost || product.purchasePrice || (item.unitPrice * 0.7)) : (item.unitPrice * 0.7);
      
      totalCost += itemCost * item.quantity;
      totalRevenue += item.unitPrice * item.quantity;
    });
    
    const profit = totalRevenue - totalCost;
    const margin = totalRevenue > 0 ? ((profit / totalRevenue) * 100).toFixed(1) : 0;
    
    return {
      profit,
      margin,
      totalCost,
      totalRevenue
    };
  };

  const handleDeleteSale = async () => {
    if (!saleToDelete) return;

    try {
      setDeleting(true);
      
      // Primero, restaurar el stock de los productos
      for (const item of saleToDelete.items) {
        // Obtener el producto actual
        const products = await getDocuments(userId, 'products', [
          { field: 'id', operator: '==', value: item.productId }
        ]);
        
        if (products.length > 0) {
          const product = products[0];
          const newStock = (product.stock || 0) + item.quantity;
          await updateDocument(userId, 'products', product.id, { stock: newStock });
        }
      }
      
      // Luego, eliminar la venta
      await deleteDocument(userId, 'sales', saleToDelete.id);
      
      // Recargar las ventas
      await loadSales();
      
      setShowDeleteModal(false);
      setSaleToDelete(null);
    } catch (error) {
      console.error('Error deleting sale:', error);
      alert('Error al eliminar la venta. Por favor, intente nuevamente.');
    } finally {
      setDeleting(false);
    }
  };

  const filteredSales = sales.filter(sale => {
    const matchesSearch = searchTerm === '' || 
      sale.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDate = dateFilter === '' || 
      (sale.createdAt || sale.date)?.startsWith(dateFilter);
    
    return matchesSearch && matchesDate;
  });

  const generateReceiptText = (sale) => {
    if (!sale) return '';
    
    let text = `🏥 *${userData?.pharmacyName || 'NomoFarma'}* 🏥\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    if (userData?.rfc) {
      text += `📍 RFC/NIT: ${userData.rfc}\n`;
    }
    if (userData?.businessAddress) {
      text += `📍 ${userData.businessAddress}\n`;
    }
    if (userData?.businessCity) {
      text += `📍 ${userData.businessCity}\n`;
    }
    if (userData?.businessPhone) {
      text += `📞 Tel: ${userData.businessPhone}\n`;
    }
    if (userData?.businessEmail) {
      text += `📧 ${userData.businessEmail}\n`;
    }
    text += `📅 ${format(new Date(sale.createdAt || sale.date), 'dd/MM/yyyy HH:mm', { locale: es })}\n`;
    text += `👤 Cliente: ${sale.customerName || 'Público General'}\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `\n🛒 *PRODUCTOS COMPRADOS*\n\n`;
    
    sale.items.forEach((item, index) => {
      text += `${index + 1}. 💊 ${item.productName}\n`;
      text += `   📦 Cantidad: ${item.quantity} x ${formatCurrency(item.unitPrice)}\n`;
      text += `   💰 Subtotal: ${formatCurrency(item.total)}\n\n`;
    });
    
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `💳 *RESUMEN DE PAGO*\n\n`;
    text += `Subtotal: ${formatCurrency(sale.subtotal)}\n`;
    
    if (sale.chargedWithTax !== false) {
      text += `IVA 16%: ${formatCurrency(sale.tax)}\n`;
    }
    
    text += `\n✅ *TOTAL A PAGAR: ${formatCurrency(sale.total)}* ✅\n\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `🙏 *¡Gracias por su compra!* 🙏\n`;
    text += `📋 Conserve este recibo para cualquier aclaración\n`;
    text += `💚 ¡Que tenga un excelente día!`;
    
    return text;
  };

  const sendWhatsApp = (sale) => {
    if (!sale) return;
    
    const text = generateReceiptText(sale);
    const encodedText = encodeURIComponent(text);
    
    let phoneNumber = sale.customerPhone || '';
    phoneNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    if (phoneNumber && !phoneNumber.startsWith('+')) {
      phoneNumber = '57' + phoneNumber;
    }
    
    const whatsappUrl = phoneNumber 
      ? `https://wa.me/${phoneNumber}?text=${encodedText}`
      : `https://wa.me/?text=${encodedText}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const handlePrint = () => {
    if (!receiptRef.current || !selectedSale) {
      console.error('No se encontró el elemento del recibo o la venta');
      alert('Error: No se puede imprimir el recibo');
      return;
    }

    try {
      // Crear un iframe oculto para imprimir
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'absolute';
      printFrame.style.top = '-10000px';
      printFrame.style.left = '-10000px';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      
      document.body.appendChild(printFrame);
      
      const printDocument = printFrame.contentWindow || printFrame.contentDocument;
      const doc = printDocument.document || printDocument;
      
      // Escribir el contenido HTML
      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Recibo_${selectedSale?.saleNumber || 'venta'}</title>
            <style>
              /* Version 2.0 - Tamaño grande para POS */
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              @page {
                size: A6;
                margin: 0;
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 20px !important;
                line-height: 1.6;
                margin: 0;
                padding: 1mm 0.5mm 30mm 0.5mm; /* Agregado 30mm de espacio inferior */
                width: 100%;
                max-width: 105mm;
              }
              .receipt {
                width: 100%;
              }
              .receipt-header {
                text-align: center;
                margin-bottom: 15px;
              }
              .receipt-header h2 {
                font-size: 32px !important;
                font-weight: bold;
                margin-bottom: 8px;
              }
              .receipt-header p {
                font-size: 18px !important;
                margin: 4px 0;
              }
              .receipt-body {
                margin: 15px 0;
              }
              .receipt-item {
                display: flex;
                justify-content: space-between;
                margin: 8px 0;
                font-size: 18px !important;
                font-weight: normal;
              }
              .receipt-item strong {
                font-weight: bold;
              }
              .divider {
                border-top: 1px dashed #000;
                margin: 10px 0;
                padding: 10px 0;
              }
              .receipt-footer {
                text-align: center;
                margin-top: 12px;
                padding-top: 12px;
                padding-bottom: 20mm; /* Espacio adicional al final */
                border-top: 1px dashed #000;
              }
              .receipt-footer p {
                font-size: 16px !important;
                margin: 4px 0;
              }
              @media print {
                html, body {
                  margin: 0;
                  padding: 0;
                  width: 100%;
                  height: auto;
                }
                body {
                  padding: 1mm 0.5mm 30mm 0.5mm; /* Mantener el espacio inferior en print */
                }
                @page {
                  margin: 0;
                  size: A6;
                }
              }
            </style>
          </head>
          <body>
            ${receiptRef.current.innerHTML}
          </body>
        </html>
      `);
      doc.close();
      
      // Esperar a que se cargue el contenido
      printFrame.onload = function() {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          
          // Remover el iframe después de imprimir
          setTimeout(() => {
            document.body.removeChild(printFrame);
            console.log('Impresión completada');
          }, 1000);
        } catch (e) {
          console.error('Error durante la impresión:', e);
          document.body.removeChild(printFrame);
        }
      };
      
    } catch (error) {
      console.error('Error al imprimir:', error);
      alert('Error al imprimir el recibo. Por favor, intente nuevamente.');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p>Cargando ventas...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <h1>Historial de Ventas</h1>
        <p>Consulta y gestiona todas las ventas realizadas</p>
      </Header>

      {filteredSales.length > 0 && (() => {
        const totalRevenue = filteredSales.reduce((sum, s) => sum + (s.total || 0), 0);
        const totalProfit = filteredSales.reduce((sum, s) => {
          const m = calculateSaleMargin(s);
          return sum + (m ? m.profit : 0);
        }, 0);
        return (
          <StatsRow>
            <StatCard
              $accent="linear-gradient(180deg, #10B981, #059669)"
              $gradient="linear-gradient(135deg, #047857, #10B981)"
            >
              <div className="label">Ventas</div>
              <div className="value">{filteredSales.length}</div>
            </StatCard>
            <StatCard
              $accent="linear-gradient(180deg, #60A5FA, #2563EB)"
              $gradient="linear-gradient(135deg, #1E40AF, #2563EB)"
            >
              <div className="label">Ingresos</div>
              <div className="value">{formatCurrency(totalRevenue)}</div>
            </StatCard>
            <StatCard
              $accent="linear-gradient(180deg, #A855F7, #7E22CE)"
              $gradient="linear-gradient(135deg, #6B21A8, #A855F7)"
            >
              <div className="label">Ganancia</div>
              <div className="value">{formatCurrency(totalProfit)}</div>
            </StatCard>
          </StatsRow>
        );
      })()}

      <FilterSection>
        <InputWrap $grow={2} $min="220px">
          <Search className="field-icon" />
          <input
            type="text"
            placeholder="Buscar por cliente o ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputWrap>
        <InputWrap $min="160px">
          <CalendarIcon className="field-icon" />
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </InputWrap>
        <Button variant="outline" onClick={() => { setSearchTerm(''); setDateFilter(''); }}>
          <X size={14} style={{ marginRight: 4 }} />
          Limpiar
        </Button>
      </FilterSection>

      {filteredSales.length === 0 ? (
        <EmptyState>No se encontraron ventas</EmptyState>
      ) : (
        <SalesGrid>
          {filteredSales.map(sale => (
            <SaleCard key={sale.id}>
              <SaleHeader>
                <SaleInfo>
                  <div className="sale-id">
                    <Hash />
                    Venta #{sale.saleNumber || sale.id.slice(-8)}
                  </div>
                  <div className="meta">
                    <CalendarIcon />
                    <span>{format(new Date(sale.createdAt || sale.date), "dd 'de' MMM 'de' yyyy · HH:mm", { locale: es })}</span>
                  </div>
                  <div className="meta">
                    <User />
                    <span className="customer">{sale.customerName || 'Público General'}</span>
                  </div>
                </SaleInfo>
                <SaleTotal>
                  <div className="total">{formatCurrency(sale.total)}</div>
                  {sale.chargedWithTax !== false && (
                    <div className="tax-info">Incl. IVA</div>
                  )}
                </SaleTotal>
              </SaleHeader>

              <ItemsList>
                {sale.items.map((item, index) => (
                  <Item key={index}>
                    <span className="name">
                      {item.productName}
                      <span className="quantity">× {item.quantity}</span>
                    </span>
                    <span className="price">{formatCurrency(item.total)}</span>
                  </Item>
                ))}
              </ItemsList>

              {(() => {
                const marginData = calculateSaleMargin(sale);
                return marginData ? (
                  <ProfitInfo>
                    <div className="icon"><TrendingUp /></div>
                    <div className="profit-content">
                      <div>
                        <span className="profit-label">Ganancia: </span>
                        <span className="profit-value">{formatCurrency(marginData.profit)}</span>
                      </div>
                      <span className="margin-pct">{marginData.margin}% margen</span>
                    </div>
                  </ProfitInfo>
                ) : null;
              })()}

              <ActionButtons>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => {
                    setSelectedSale(sale);
                    setShowReceipt(true);
                  }}
                >
                  <ReceiptIcon size={14} style={{ marginRight: 4 }} />
                  Ver Recibo
                </Button>
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => {
                    setSaleToDelete(sale);
                    setShowDeleteModal(true);
                  }}
                >
                  <Trash2 size={14} style={{ marginRight: 4 }} />
                  Eliminar
                </Button>
              </ActionButtons>
            </SaleCard>
          ))}
        </SalesGrid>
      )}

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSaleToDelete(null);
        }}
        title="Confirmar Eliminación"
      >
        <ConfirmModal>
          <p>¿Está seguro que desea eliminar esta venta?</p>
          
          <div className="warning">
            <strong>⚠️ Advertencia:</strong> Esta acción no se puede deshacer. 
            El stock de los productos será restaurado automáticamente.
          </div>
          
          {saleToDelete && (
            <div>
              <p><strong>Venta:</strong> #{saleToDelete.saleNumber || saleToDelete.id.slice(-8)}</p>
              <p><strong>Cliente:</strong> {saleToDelete.customerName || 'Público General'}</p>
              <p><strong>Total:</strong> {formatCurrency(saleToDelete.total)}</p>
              <p><strong>Productos:</strong></p>
              <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                {saleToDelete.items.map((item, index) => (
                  <li key={index}>
                    {item.productName} x{item.quantity}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteModal(false);
                setSaleToDelete(null);
              }}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteSale}
              disabled={deleting}
            >
              <Trash2 size={14} style={{ marginRight: 4 }} />
              {deleting ? 'Eliminando...' : 'Sí, Eliminar'}
            </Button>
          </div>
        </ConfirmModal>
      </Modal>

      <Modal
        isOpen={showReceipt}
        onClose={() => {
          setShowReceipt(false);
          setSelectedSale(null);
        }}
        title="Recibo de Venta"
        footer={
          <>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowReceipt(false);
                setSelectedSale(null);
              }}
            >
              Cerrar
            </Button>
            <Button
              variant="success"
              onClick={() => {
                if (selectedSale) {
                  sendWhatsApp(selectedSale);
                }
              }}
            >
              <MessageCircle size={16} style={{ marginRight: 4 }} />
              WhatsApp
            </Button>
            <Button
              onClick={() => {
                if (selectedSale && receiptRef.current) {
                  handlePrint();
                }
              }}
            >
              <Printer size={16} style={{ marginRight: 4 }} />
              Imprimir
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (selectedSale) {
                  await shareReceiptPDF(selectedSale, userData);
                }
              }}
            >
              <FileDown size={16} style={{ marginRight: 4 }} />
              Compartir PDF
            </Button>
          </>
        }
      >
        {selectedSale && (
          <Receipt ref={receiptRef} className="receipt">
            <ReceiptHeader className="receipt-header">
              <h2>{userData?.pharmacyName || 'NomoFarma'}</h2>
              {userData?.rfc && <p>RFC/NIT: {userData.rfc}</p>}
              {userData?.businessAddress && <p>{userData.businessAddress}</p>}
              {userData?.businessCity && <p>{userData.businessCity}</p>}
              {userData?.businessPhone && <p>Tel: {userData.businessPhone}</p>}
              {userData?.businessEmail && <p>{userData.businessEmail}</p>}
              <p>{format(new Date(selectedSale.createdAt || selectedSale.date), 'dd/MM/yyyy HH:mm', { locale: es })}</p>
              <p style={{ marginTop: '10px', borderTop: '1px dashed #000', paddingTop: '10px' }}>
                Cliente: {selectedSale.customerName || 'Público General'}
              </p>
            </ReceiptHeader>
            
            <ReceiptBody className="receipt-body">
              <div className="divider">
                {selectedSale.items.map((item, index) => (
                  <div key={index}>
                    <ReceiptItem className="receipt-item">
                      <span>{item.productName}</span>
                    </ReceiptItem>
                    <ReceiptItem className="receipt-item">
                      <span>{item.quantity} x {formatCurrency(item.unitPrice)}</span>
                      <span>{formatCurrency(item.total)}</span>
                    </ReceiptItem>
                  </div>
                ))}
              </div>
              
              <div style={{ marginTop: '10px' }}>
                <ReceiptItem className="receipt-item">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(selectedSale.subtotal)}</span>
                </ReceiptItem>
                {selectedSale.chargedWithTax !== false && (
                  <ReceiptItem className="receipt-item">
                    <span>IVA 16%:</span>
                    <span>{formatCurrency(selectedSale.tax)}</span>
                  </ReceiptItem>
                )}
                <ReceiptItem className="receipt-item" style={{ fontWeight: 'bold', fontSize: '24px', marginTop: '10px', paddingTop: '8px', borderTop: '2px solid #000' }}>
                  <span>TOTAL:</span>
                  <span>{formatCurrency(selectedSale.total)}</span>
                </ReceiptItem>
              </div>
            </ReceiptBody>
            
            <ReceiptFooter className="receipt-footer">
              <p>¡Gracias por su compra!</p>
              <p>Conserve su ticket</p>
              <p>No. Ticket: {selectedSale.saleNumber || selectedSale.id.slice(-8)}</p>
            </ReceiptFooter>
          </Receipt>
        )}
      </Modal>
    </PageContainer>
  );
};